import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { AdminThemeToggle } from '../../components/theme/AdminThemeToggle'

/**
 * ADMIN-ONLY login redesign (futuristic dark-first split screen).
 * Auth logic untouched: username + password -> login() -> /admin/dashboard.
 * The right-side visual panel is purely decorative (aria-hidden) and shows
 * NO real business data.
 */
export function AdminLoginPage() {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const { login } = useAuth()
  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      await login(username, password)
      navigate('/admin/dashboard')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login gagal')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="admin-login-root">
      <div className="admin-login-theme">
        <AdminThemeToggle variant="login" />
      </div>

      <div className="admin-login-card">
        {/* LEFT: brand + form */}
        <div className="admin-login-form-pane">
          <div className="admin-login-brand">
            <span className="admin-login-logo" aria-hidden="true">B</span>
            <div>
              <div className="admin-login-brand-name">CV Berkah Nusa Sejati</div>
              <div className="admin-login-brand-sub">Admin Panel</div>
            </div>
          </div>

          <p className="admin-login-kicker">Selamat datang kembali</p>
          <h1 className="admin-login-title">
            Admin <span className="thin">Login</span>
          </h1>
          <p className="admin-login-subtitle">
            Manage your store operations from one place.
          </p>

          {error && (
            <div className="admin-login-error" role="alert">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="admin-login-form" noValidate={false}>
            <div className="admin-login-field">
              <label htmlFor="username">Username</label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username"
                required
                autoFocus
                autoComplete="username"
              />
            </div>

            <div className="admin-login-field">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                autoComplete="current-password"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="admin-login-submit"
            >
              {isLoading && <span className="admin-login-spinner" aria-hidden="true" />}
              {isLoading ? 'Memproses...' : 'Sign In'}
            </button>
          </form>

          <p className="admin-login-footnote">
            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            Restricted area. Authorized administrators only.
          </p>
        </div>

        {/* RIGHT: decorative system visual (illustrative only, no real data) */}
        <div className="admin-login-visual" aria-hidden="true">
          <span className="adm-deco-orb" />
          <p className="admin-login-visual-caption">Warung Grosir System</p>
          <h2 className="admin-login-visual-title">
            One console for products, orders &amp; receipts.
          </h2>

          <div className="admin-login-visual-grid">
            <div className="adm-deco-card wide">
              <div className="adm-deco-label">Activity Overview</div>
              <div className="adm-deco-bars">
                <span style={{ height: '38%' }} />
                <span style={{ height: '62%' }} />
                <span style={{ height: '46%' }} />
                <span style={{ height: '80%' }} />
                <span style={{ height: '55%' }} />
                <span style={{ height: '92%' }} />
                <span style={{ height: '68%' }} />
                <span style={{ height: '50%' }} />
              </div>
            </div>

            <div className="adm-deco-card">
              <div className="adm-deco-label">Queue</div>
              <div className="adm-deco-row">
                <span className="adm-deco-dot" />
                <span className="adm-deco-lines"><i /><i /></span>
              </div>
              <div className="adm-deco-row">
                <span className="adm-deco-dot" />
                <span className="adm-deco-lines"><i /><i /></span>
              </div>
              <div className="adm-deco-row">
                <span className="adm-deco-dot" />
                <span className="adm-deco-lines"><i /><i /></span>
              </div>
            </div>

            <div className="adm-deco-card">
              <div className="adm-deco-label">Print Status</div>
              <div className="adm-deco-ring" />
              <div style={{ textAlign: 'center' }}>
                <span className="adm-deco-pill">READY</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
